<template>
  <div v-if="show" :class="classes" role="alert">
    <UiIcon v-if="iconName" :name="iconName" class="shrink-0" />
    <div class="flex-1"><slot /></div>
    <button
      v-if="dismissible"
      type="button"
      class="shrink-0 opacity-70 hover:opacity-100"
      aria-label="close"
      @click="close"
    >
      <UiIcon name="x" :size="18" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import UiIcon from './UiIcon.vue'

/** UiAlert — Vuetify <v-alert> replacement (type-based styling).
 * Dual Vue 2 / Vue 3 compatible.
 */
const props = withDefaults(
  defineProps<{
    type?: 'info' | 'success' | 'warning' | 'error'
    dense?: boolean
    text?: boolean
    dismissible?: boolean
    modelValue?: boolean
    value?: boolean
  }>(),
  {
    type: 'info',
    dense: false,
    text: false,
    dismissible: false,
    modelValue: undefined,
    value: undefined,
  },
)

const emit = defineEmits<{
  (e: 'dismiss'): void
  (e: 'update:modelValue', value: boolean): void
}>()

const show = computed(() => props.modelValue ?? props.value ?? true)

function close() {
  emit('update:modelValue', false)
  emit('dismiss')
}

const iconName = computed(() => {
  const map = {
    info: 'info',
    success: 'check_circle',
    warning: 'warning',
    error: 'error',
  }
  return map[props.type]
})

const typeClass = computed(() => {
  const map: Record<string, string> = props.text
    ? {
        info: 'bg-info/10 text-info',
        success: 'bg-success/10 text-success',
        warning: 'bg-warning/10 text-warning',
        error: 'bg-error/10 text-error',
      }
    : {
        info: 'bg-info text-white',
        success: 'bg-success text-white',
        warning: 'bg-warning text-white',
        error: 'bg-error text-white',
      }
  return map[props.type]
})

const classes = computed(() =>
  [
    'flex items-center gap-2 rounded-lg w-full',
    props.dense ? 'px-3 py-1.5 text-sm' : 'px-4 py-3 text-sm',
    typeClass.value,
  ].join(' '),
)
</script>
